/**
 * Drives the native update prompt / mandatory update gate.
 *
 * - Runs after mount, each time the app foregrounds, and on every route change
 *   (route changes only re-evaluate the last result — no extra network call).
 * - Independent of authentication, sessions and every workflow.
 * - "optional": dismissable once per session ("Continue for now").
 * - "required": non-dismissable, but DEFERRED while an appraisal is active
 *   (isProtectedAppraisalWork: active workflow OR saved resumable draft,
 *   decided synchronously at render). Entering an appraisal route with no
 *   active workflow or draft for that job
 *   is blocked before the appraisal is created.
 * - Never reloads, navigates, or clears storage/queues. Any failure → no prompt.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  type AppUpdatePolicy,
  type UpdatePlatform,
  type UpdateStatus,
  dismissForSession,
  evaluateUpdateStatus,
  fetchUpdatePolicy,
  getInstalledVersion,
  getNativeInstalledVersion,
  getUpdatePlatform,
  isDismissedThisSession,
  startAppUpdate,
} from "@/lib/appUpdate";
import { isProtectedAppraisalWork } from "@/lib/activeWorkflowGuard";

export interface AppUpdateGateState {
  status: UpdateStatus;
  policy: AppUpdatePolicy | null;
  installedVersion: string | null;
  visible: boolean;
  /** True when the blocking "Update required" screen should be shown. */
  blocking: boolean;
  /** Locally queued items still to sync (shown on the blocking screen). */
  pendingCount: number;
  updating: boolean;
  onUpdateNow: () => void;
  onLater: () => void;
  recheck: () => void;
}

async function countPendingWork(): Promise<number> {
  let n = 0;
  try {
    const { syncQueue } = await import("@/lib/offlineDb");
    n += (await syncQueue.getAll()).length;
  } catch { /* web / unavailable */ }
  try {
    const { getPendingCompletionWorkflowCount } = await import("@/lib/completionWorkflowQueue");
    n += getPendingCompletionWorkflowCount();
  } catch { /* ignore */ }
  return n;
}

export function useAppUpdateGate(): AppUpdateGateState {
  const location = useLocation();
  const [status, setStatus] = useState<UpdateStatus>("current");
  const [policy, setPolicy] = useState<AppUpdatePolicy | null>(null);
  const [installedVersion, setInstalledVersion] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const mounted = useRef(true);
  const running = useRef(false);

  const runCheck = useCallback(async () => {
    if (running.current) return;
    running.current = true;
    try {
      const platform: UpdatePlatform | null = await getUpdatePlatform();
      if (!platform) return; // web — store policy never applies
      const nativeVersion = await getNativeInstalledVersion();
      const version = nativeVersion ?? (await getInstalledVersion());
      const { policy: fetched, confirmedAt } = await fetchUpdatePolicy(platform);
      const next = evaluateUpdateStatus(version, fetched, {
        nativeVersionKnown: nativeVersion !== null,
        confirmedAt,
      });
      if (!mounted.current) return;
      setInstalledVersion(version);
      setPolicy(fetched);
      setStatus(next);
      setDismissed(
        next === "optional" && fetched
          ? isDismissedThisSession(fetched.latestVersion)
          : false,
      );
      if (next === "required") setPendingCount(await countPendingWork());
    } catch {
      // A failed check must never affect the app.
    } finally {
      running.current = false;
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    void runCheck();

    let remove: (() => void) | undefined;
    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const handle = await App.addListener("appStateChange", ({ isActive }) => {
          // Re-check on foreground: covers returning from the App/Play Store.
          if (isActive) void runCheck();
        });
        remove = () => {
          void handle.remove();
        };
      } catch {
        /* web */
      }
    })();

    return () => {
      mounted.current = false;
      remove?.();
    };
  }, [runCheck]);

  // Refresh the syncing count on route change (cheap, local only).
  useEffect(() => {
    if (status === "required") void countPendingWork().then((n) => mounted.current && setPendingCount(n));
  }, [location.pathname, status]);

  const onUpdateNow = useCallback(() => {
    if (!policy) return;
    setUpdating(true);
    void startAppUpdate(policy, { immediate: status === "required" }).finally(() => {
      if (mounted.current) setUpdating(false);
    });
  }, [policy, status]);

  const onLater = useCallback(() => {
    if (status === "required") return; // no bypass
    if (policy) dismissForSession(policy.latestVersion);
    setDismissed(true);
  }, [policy, status]);

  // B1: final display decision is synchronous at render time, so the blocker
  // can never render (even for one frame) over in-progress/saved appraisal work.
  const blocking =
    status === "required" && !!policy && !isProtectedAppraisalWork(location.pathname);
  const visible = blocking || (status === "optional" && !dismissed && !!policy);

  return {
    status,
    policy,
    installedVersion,
    visible,
    blocking,
    pendingCount,
    updating,
    onUpdateNow,
    onLater,
    recheck: () => void runCheck(),
  };
}
